import { TypedStorage } from "@utils/storage";
import { STORAGE_KEYS } from "@constants/storage-keys";
import { isFilenamesStorage } from "@validation/filenames.schema";
import type { FilenamesStorage, FilePathTuple } from "@app-types/storage.types";
import type { ElementSelectData } from "@app-types/element.types";

const storage = new TypedStorage<FilenamesStorage>(
  STORAGE_KEYS.FILENAMES,
  isFilenamesStorage
);

/**
 * Get all filenames from localStorage
 */
export function getFilenames(): FilenamesStorage {
  return storage.getOrDefault({});
}

/**
 * Set filenames for a specific group (replaces entire group)
 */
export function setGroupFilenames(
  groupId: string,
  groupFilenames: FilePathTuple[]
): void {
  const filenames = getFilenames();
  filenames[groupId] = groupFilenames;
  storage.set(filenames);
}

/**
 * Remove filenames for a specific group
 */
export function removeGroupFilenames(groupId: string): void {
  const filenames = getFilenames();
  delete filenames[groupId];
  storage.set(filenames);
}

/**
 * Clear all filenames
 */
export function clearFilenames(): void {
  storage.clear();
}

/**
 * Build file paths based on element attributes
 * @param data - Element data from button
 * @param filenames - Array of filename strings from hidden inputs
 * @returns Array of [storagePath, downloadName] tuples
 */
export function buildFilePaths(
  data: ElementSelectData,
  filenames: string[]
): FilePathTuple[] {
  const { category, groupName, elementId, isHd, isAnimated, isMultiple, frames } =
    data;
  const result: FilePathTuple[] = [];

  // Handle sounds category - always single .wav file, no @2x
  if (category === "sounds") {
    const baseFilename = filenames[0] ?? groupName;
    result.push([
      `${category}/${groupName}/${elementId}.wav`,
      `${baseFilename}.wav`,
    ]);
    return result;
  }

  if (isMultiple === "1") {
    // Multiple files: each filename is independent
    filenames.forEach((filename) => {
      // Always add non-@2x version
      result.push([
        `${category}/${groupName}/${elementId}/${filename}.png`,
        `${filename}.png`,
      ]);
      // If HD, also add @2x version
      if (isHd === "1") {
        result.push([
          `${category}/${groupName}/${elementId}/${filename}@2x.png`,
          `${filename}@2x.png`,
        ]);
      }
    });
  } else if (isAnimated === "1") {
    // Animated: loop from 0 to frames
    const frameCount = parseInt(frames, 10) || 0;
    const baseFilename = filenames[0] ?? groupName;

    for (let i = 0; i <= frameCount; i++) {
      // Always add non-@2x version
      result.push([
        `${category}/${groupName}/${elementId}-${i}.png`,
        `${baseFilename}-${i}.png`,
      ]);
      // If HD, also add @2x version
      if (isHd === "1") {
        result.push([
          `${category}/${groupName}/${elementId}-${i}@2x.png`,
          `${baseFilename}-${i}@2x.png`,
        ]);
      }
    }
  } else {
    // Single file (not animated, not multiple)
    const baseFilename = filenames[0] ?? groupName;
    // Always add non-@2x version
    result.push([
      `${category}/${groupName}/${elementId}.png`,
      `${baseFilename}.png`,
    ]);
    // If HD, also add @2x version
    if (isHd === "1") {
      result.push([
        `${category}/${groupName}/${elementId}@2x.png`,
        `${baseFilename}@2x.png`,
      ]);
    }
  }

  return result;
}
